import * as React from 'react';

// Pointer dwell inside the scroll area before the inner scroll takes over; tune here.
export const DEFAULT_DWELL_MS = 300;

const findScrollParent = (start: HTMLElement | null): HTMLElement | Window => {
  let el = start?.parentElement ?? null;
  while (el) {
    const overflowY = getComputedStyle(el).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }

  return window;
};

// Normalize line/page wheel deltas to pixels.
const deltaToPixels = (event: WheelEvent, viewport: number): number => {
  if (event.deltaMode === 1) return event.deltaY * 16;
  if (event.deltaMode === 2) return event.deltaY * viewport;

  return event.deltaY;
};

const scrollTargetBy = (target: HTMLElement | Window, delta: number): void => {
  if (target instanceof Window) target.scrollBy(0, delta);
  else target.scrollTop += delta;
};

// Inner scroll captures the wheel only after the pointer dwells ~dwellMs; until then each wheel is
// redirected to the surrounding scroll parent (and re-arms the timer) so the page scroll isn't hijacked.
// `enabled` (the caller's mount flag, e.g. part.isExpanded) is a dependency so the effect re-runs when
// the scroll element actually mounts — the results area is rendered conditionally, so an element that
// appears after the first render (e.g. the first, auto-expanded part) would otherwise get no listeners.
export const useDwellScroll = (
  dwellMs: number,
  enabled: boolean
): React.RefObject<HTMLDivElement> => {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    let active = false;
    let timer: number | undefined;

    const arm = (): void => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        active = true;
      }, dwellMs);
    };

    const onEnter = (): void => arm();

    const onLeave = (): void => {
      window.clearTimeout(timer);
      active = false;
    };

    const onWheel = (event: WheelEvent): void => {
      if (active) return;
      event.preventDefault();
      scrollTargetBy(findScrollParent(el), deltaToPixels(event, el.clientHeight));
      arm();
    };

    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      window.clearTimeout(timer);
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      el.removeEventListener('wheel', onWheel);
    };
  }, [dwellMs, enabled]);

  return ref;
};
